import { useMemo } from "react";

import { useEvents } from "@/context/events";
import {
	canApplyTimeOfDayFilters,
	getTimeFilterZoneBlockedMessage,
	isTimeFilterZoneBlocked,
	resolveTimeFilterZone,
	type TimeFilterZoneResolution,
} from "@/features/events/utils/resolve-time-filter-zone";
import { useGetLocations } from "@/hooks/useLocations";

export type UseTimeFilterZoneResult = {
	/** User-facing copy when isTimeFilterBlocked is true. */
	blockedMessage: string;
	/**
	 * True when a single location timezone is resolved so query/matcher bounds
	 * can use that timezone. When false, `buildDateTimeRangeTimestamps` still
	 * applies times via the browser local timezone.
	 */
	canApplyTimeFilters: boolean;
	/**
	 * True when UI should show a timezone warning (unsafe timezone or locations
	 * load failure). False while locations are still loading.
	 */
	isTimeFilterBlocked: boolean;
	/** Full resolution for callers that need the status enum. */
	resolution: TimeFilterZoneResolution;
	/** IANA timezone when status is ok; otherwise null. */
	timeZone: null | string;
};

/**
 * Resolve time-filter timezone from an explicit locationsFilter.
 *
 * Safe outside EventFilterScopeProvider (e.g. app-level WebSocketProvider):
 * only needs locations data + the filter ID list, not useEvents()/scope.
 *
 * Returns `pending` until `useGetLocations` succeeds so callers do not treat
 * an empty list as unavailable.
 * Returns `error` if the locations query failed with no cached data (message
 * shown, filters kept). A failed background refetch keeps using cached data.
 */
export function useResolveTimeFilterZone(
	locationsFilter: readonly string[],
): UseTimeFilterZoneResult {
	const { isLocationsError, isLocationsSuccess, locationsData } =
		useGetLocations();

	const resolution = useMemo((): TimeFilterZoneResolution => {
		const cachedLocations = locationsData ?? [];
		if (cachedLocations.length > 0) {
			return resolveTimeFilterZone(locationsFilter, cachedLocations);
		}
		if (isLocationsError) {
			return { status: "error" };
		}
		if (!isLocationsSuccess) {
			return { status: "pending" };
		}
		return resolveTimeFilterZone(locationsFilter, cachedLocations);
	}, [isLocationsError, isLocationsSuccess, locationsData, locationsFilter]);

	const canApplyTimeFilters = canApplyTimeOfDayFilters(resolution);
	const timeZone = resolution.status === "ok" ? resolution.timeZone : null;

	return {
		blockedMessage: getTimeFilterZoneBlockedMessage(resolution),
		canApplyTimeFilters,
		isTimeFilterBlocked: isTimeFilterZoneBlocked(resolution),
		resolution,
		timeZone,
	};
}

/**
 * Scoped variant for Advanced Search / date-time picker.
 * Uses useEvents() (requires EventFilterScopeProvider).
 *
 * Times stay on `dateRangeFilter` even when the timezone is ambiguous — the API
 * and WebSocket matcher fall back to browser-local bounds (still with times
 * and the end-minute buffer). We only surface a warning; we do not rewrite the
 * user's times back to full days.
 */
export function useTimeFilterZone(): UseTimeFilterZoneResult {
	const { locationsFilter } = useEvents();
	return useResolveTimeFilterZone(locationsFilter);
}
